import React, { useMemo } from 'react';
import { useActivityLogs, ActivityLog } from '../../activity-logs/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface UserActivityChartProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  limit?: number;
}

const UserActivityChart: React.FC<UserActivityChartProps> = ({
  embedded,
  settings,
  limit = 8,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { data: logs, isLoading } = useActivityLogs();
  const { colors } = useTheme();

  const chartData = useMemo(() => {
    const arr = Array.isArray(logs) ? logs : [];
    const byUser: Record<string, number> = {};
    arr.forEach((log: ActivityLog) => {
      const name =
        log.user?.userDisplayName ||
        log.user?.userFirstName ||
        log.user?.userEmail ||
        `User #${log.userId}`;
      byUser[name] = (byUser[name] || 0) + 1;
    });
    return Object.entries(byUser)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, effectiveLimit);
  }, [logs, effectiveLimit]);

  const cardClass = embedded
    ? 'min-w-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Activity by User
        </h2>
        <div className="h-48 flex items-center justify-center">
          <div className={`animate-pulse ${colors.textMuted}`}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Activity by User
        </h2>
        <div className="h-48 flex items-center justify-center">
          <p className={colors.textMuted}>No activity data</p>
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...chartData.map((d) => d.count), 1);

  return (
    <div className={cardClass}>
      <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
        Activity by User
      </h2>
      <div className="flex flex-col gap-2">
        {chartData.map((d) => (
          <div key={d.name} className="flex items-center gap-3">
            <span
              className={`text-xs flex-shrink-0 truncate max-w-[100px] sm:max-w-[140px] ${colors.text}`}
              title={d.name}
            >
              {d.name}
            </span>
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <div
                className="h-5 rounded bg-blue-500 dark:bg-blue-600 flex-shrink-0 transition-all"
                style={{
                  width: `${Math.max(4, (d.count / maxCount) * 100)}%`,
                  minWidth: d.count > 0 ? 8 : 0,
                }}
              />
              <span className={`text-xs ${colors.textMuted} flex-shrink-0`}>
                {d.count}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserActivityChart;
